/**
 * Dark Theme Definition
 * Pixel-perfect reproduction of current application styling
 * Based on existing variables.css values
 */

import { Theme, SemanticTokens } from '../tokens';
import { primitiveTokens } from '../tokens/primitive';
import { semanticSpacing, semanticSizing, semanticTypography, semanticAnimations, semanticMotion, semanticBreakpoints, semanticBorders } from '../tokens/semantic';

// Dark theme semantic tokens implementation
const darkSemanticTokens: SemanticTokens = {
  // Text colors - based on current dark theme text color system
  colors: {
    text: {
      primary: primitiveTokens.colors.white,                    // --semantic-colors-text-primary: #ffffff
      secondary: primitiveTokens.colors.gray[300],              // --semantic-colors-text-secondary: #b8c5d6 (close)
      muted: primitiveTokens.colors.gray[500],                  // --semantic-colors-text-secondary: #7a8394 (close)
      inverse: primitiveTokens.colors.gray[900],                // For light backgrounds
      disabled: primitiveTokens.colors.gray[600],               // Disabled text state
    },

    // Background colors - enhanced with vibrant biography patterns
    background: {
      primary: primitiveTokens.colors.gray[900],                // --semantic-colors-background-primary: #1a1a2e (close)
      secondary: primitiveTokens.colors.gray[800],              // --semantic-colors-background-secondary: #16213e (close)
      tertiary: primitiveTokens.colors.gray[700],               // Third level background
      card: 'rgba(255, 255, 255, 0.05)',                       // --semantic-colors-background-card: rgba(255, 255, 255, 0.05)
      cardHover: 'rgba(255, 255, 255, 0.1)',                   // --semantic-colors-background-cardHover: rgba(255, 255, 255, 0.1)
      overlay: 'rgba(0, 0, 0, 0.75)',                          // Modal overlay
      inverse: primitiveTokens.colors.white,                    // For light theme
      elevated: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',  // Elevated elements
      surface: 'rgba(255, 255, 255, 0.03)',                    // Surface elements
      surfaceHover: 'rgba(255, 255, 255, 0.08)',               // Surface hover state
      accent: 'rgba(233, 69, 96, 0.1)',                        // Accent background
      hover: 'rgba(255, 255, 255, 0.05)',                      // Hover background
    },

    // Border colors - based on current dark theme border system
    border: {
      default: 'rgba(255, 255, 255, 0.1)',                     // --semantic-colors-border-default: rgba(255, 255, 255, 0.1)
      light: 'rgba(255, 255, 255, 0.05)',                      // Lighter border
      heavy: 'rgba(255, 255, 255, 0.2)',                       // Heavier border
      focus: '#e94560',                                         // --semantic-colors-border-focus: #e94560
      error: '#ef4444',                                         // Error border
      success: '#22c55e',                                       // Success border
      warning: '#f97316',                                       // Warning border
    },

    // Interactive colors - based on current dark theme action colors
    interactive: {
      primary: '#e94560',                                       // --semantic-colors-interactive-primary: #e94560
      primaryHover: primitiveTokens.colors.red[500],            // Lighter primary for hover
      primaryActive: primitiveTokens.colors.red[700],           // Darker primary for active
      secondary: '#3498db',                                     // --semantic-colors-interactive-secondary: #3498db
      secondaryHover: primitiveTokens.colors.blue[400],         // Lighter secondary for hover
      secondaryActive: primitiveTokens.colors.blue[600],        // Darker secondary for active
      accent: '#f39c12',                                        // --semantic-colors-interactive-accent: #f39c12
      accentHover: primitiveTokens.colors.orange[400],          // Lighter accent for hover
      accentActive: primitiveTokens.colors.orange[600],         // Darker accent for active
    },

    // Feedback colors - semantic states for dark theme
    feedback: {
      success: '#28a745',                                       // --semantic-colors-status-success: #28a745
      successHover: primitiveTokens.colors.green[400],          // Success hover
      warning: '#f39c12',                                       // Warning state
      warningHover: primitiveTokens.colors.orange[400],         // Warning hover
      error: '#e74c3c',                                         // Error state
      errorHover: 'rgba(231, 76, 60, 0.8)',                    // Error hover with opacity
      'error-hover': 'rgba(231, 76, 60, 0.8)',                 // Alternative naming
      info: primitiveTokens.colors.blue[500],                   // Information state
      infoHover: primitiveTokens.colors.blue[400],              // Information hover
    },

    // RPG-specific colors - based on current dark theme RPG color system
    rpg: {
      hp: '#e74c3c',                                            // --semantic-colors-rpg-hp: #e74c3c
      hpHover: primitiveTokens.colors.red[500],                 // HP hover
      stress: '#9b59b6',                                        // --semantic-colors-rpg-stress: #9b59b6
      stressHover: primitiveTokens.colors.purple[400],          // Stress hover
      armor: '#3498db',                                         // --semantic-colors-rpg-armor: #3498db
      armorHover: primitiveTokens.colors.blue[500],             // Armor hover
      hope: '#f39c12',                                          // --semantic-colors-rpg-hope: #f39c12
      hopeHover: primitiveTokens.colors.orange[500],            // Hope hover
      focus: '#2980b9',                                         // --semantic-colors-rpg-focus: #2980b9
      focusHover: primitiveTokens.colors.blue[600],             // Focus hover

      // Priority system colors (from biography)
      priority: {
        high: '#f59e0b',       // High priority accent (amber) - from biography
        medium: '#8b5cf6',     // Medium priority accent (purple) - from biography
        low: '#10b981',        // Low priority accent (emerald) - from biography
      },

      // Class-specific colors (from biography)
      classes: {
        warrior: '#e53e3e',    // Warrior red - from biography
        brawler: '#dd6b20',    // Brawler orange - from biography
        guardian: '#3182ce',   // Guardian blue - from biography
        ranger: '#38a169',     // Ranger green - from biography
        rogue: '#805ad5',      // Rogue purple - from biography
        seraph: '#ecc94b',     // Seraph yellow - from biography
        wizard: '#4299e1',     // Wizard blue - from biography
        sorcerer: '#ed64a6',   // Sorcerer pink - from biography
        bard: '#f687b3',       // Bard pink - from biography
        druid: '#48bb78',      // Druid green - from biography
      },
    },

    // Status colors (additional semantic meanings)
    status: {
      success: '#28a745',
      warning: '#f39c12',
      error: '#e74c3c',
      info: '#3498db',
    },
  },

  // Dark theme gradient tokens based on biography system
  gradients: {
    // Icon gradients (biography analogous progression)
    icons: {
      identity: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',      // Deep blue gradient
      appearance: 'linear-gradient(135deg, #0369a1 0%, #0891b2 100%)',    // Blue to teal gradient
      background: 'linear-gradient(135deg, #0891b2 0%, #0d9488 100%)',    // Teal to green gradient
      connections: 'linear-gradient(135deg, #0d9488 0%, #059669 100%)',   // Green gradient
      items: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',         // Green to lime gradient
      custom: 'linear-gradient(135deg, #22c55e 0%, #84cc16 100%)',        // Lime to yellow-green gradient
      journal: 'linear-gradient(135deg, #84cc16 0%, #a3e635 100%)',       // Yellow-green gradient
    },

    // Background gradients
    backgrounds: {
      primary: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',  // Biography primary gradient
      secondary: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',             // Biography secondary gradient
      card: 'linear-gradient(135deg, #374151 0%, #4a5568 50%)',                   // Biography card gradient
      elevated: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)',              // Elevated element gradient
    },

    // Interactive gradients
    interactive: {
      primary: 'linear-gradient(135deg, #7c2d2d 0%, #8b3a3a 100%)',       // Primary action gradient (muted but clearly red for dark theme)
      secondary: 'linear-gradient(135deg, #2d4a7c 0%, #3a5a8b 100%)',     // Secondary action gradient (muted but clearly blue for dark theme)
      accent: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',        // Accent gradient
      hover: 'linear-gradient(135deg, #4a5568 0%, #5a6578 100%)',         // Hover state gradient
    },
  },

  // Dark theme shadow tokens based on biography system - enhanced for depth and vibrancy
  shadows: {
    base: '0 4px 6px rgba(0, 0, 0, 0.1)',           // Base shadow
    hover: '0 6px 12px rgba(0, 0, 0, 0.15)',        // Hover shadow with subtle lift
    elevated: '0 8px 25px rgba(0, 0, 0, 0.15)',     // Elevated shadow for depth
    interactive: '0 6px 20px rgba(0, 0, 0, 0.15)',  // Interactive elements shadow
    focus: '0 0 0 3px rgba(233, 69, 96, 0.3)',      // Focus ring shadow
    // Elevation system shadows
    low: '0 2px 4px rgba(0, 0, 0, 0.1)',            // Low elevation
    medium: '0 4px 8px rgba(0, 0, 0, 0.15)',        // Medium elevation
    high: '0 8px 16px rgba(0, 0, 0, 0.2)',          // High elevation
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',     // Text shadow for depth
  },

  // Use theme-agnostic tokens from semantic.ts
  borders: semanticBorders,
  spacing: semanticSpacing,
  sizing: semanticSizing,
  typography: semanticTypography,
  animations: semanticAnimations,
  motion: semanticMotion,
  breakpoints: semanticBreakpoints,
};

export const darkTheme: Theme = {
  metadata: {
    name: 'dark',
    displayName: 'Dark Theme',
    description: 'The classic dark theme with RPG-focused design',
    version: '1.0.0',
    author: 'Søren Haug',
    isDark: true,
    isDefault: true,
  },
  tokens: {
    primitive: primitiveTokens,
    semantic: darkSemanticTokens,
  },
};

export default darkTheme;
